/**
 * Project Configuration Constants
 * Shared multiplier values for quality and energy standards
 * Used by both frontend UI and backend cost generation
 */

import {
  StandardLevel,
  EnergyStandardLevel,
} from '../api/feasibility-study/revenue.types'

export interface StandardOption {
  value: StandardLevel
  labelKey: string
  label: { en: string; de: string; fr: string; it: string }
  multiplier: number
}

export interface EnergyStandardOption {
  value: EnergyStandardLevel
  labelKey: string
  label: { en: string; de: string; fr: string; it: string }
  multiplier: number
}

/**
 * Quality standard options with multipliers
 * Based on Swiss construction quality levels (SIA norms)
 */
export const STANDARD_OPTIONS: StandardOption[] = [
  {
    value: 'einfach',
    labelKey: 'einfach',
    label: {
      en: 'Simple',
      de: 'Einfach',
      fr: 'Simple',
      it: 'Semplice',
    },
    multiplier: 0.85,
  },
  {
    value: 'ueblich',
    labelKey: 'ueblich',
    label: {
      en: 'Common',
      de: 'Üblich',
      fr: 'Courant',
      it: 'Comune',
    },
    multiplier: 0.95,
  },
  {
    value: 'durchschnittlich',
    labelKey: 'durchschnittlich',
    label: {
      en: 'Average',
      de: 'Durchschnittlich',
      fr: 'Moyen',
      it: 'Medio',
    },
    multiplier: 1.0,
  },
  {
    value: 'durchschnittlich_gehoben',
    labelKey: 'durchschnittlich_gehoben',
    label: {
      en: 'Above Average',
      de: 'Durchschnittlich-gehoben',
      fr: 'Moyen-supérieur',
      it: 'Medio-elevato',
    },
    multiplier: 1.1,
  },
  {
    value: 'gehoben',
    labelKey: 'gehoben',
    label: {
      en: 'Elevated',
      de: 'Gehoben',
      fr: 'Élevé',
      it: 'Elevato',
    },
    multiplier: 1.2,
  },
  {
    value: 'luxus',
    labelKey: 'luxus',
    label: {
      en: 'Luxury',
      de: 'Luxus',
      fr: 'Luxe',
      it: 'Lusso',
    },
    multiplier: 1.4,
  },
]

/**
 * Energy standard options with multipliers
 * Based on Swiss energy efficiency levels (Minergie, etc.)
 */
export const ENERGY_STANDARD_OPTIONS: EnergyStandardOption[] = [
  {
    value: '1.0',
    labelKey: 'keineIsolation',
    label: {
      en: 'No Insulation',
      de: 'Keine Isolation',
      fr: 'Sans isolation',
      it: 'Senza isolamento',
    },
    multiplier: 0.9,
  },
  {
    value: '2.0',
    labelKey: 'minimalIsolation',
    label: {
      en: 'Minimal Insulation',
      de: 'Minimale Isolation',
      fr: 'Isolation minimale',
      it: 'Isolamento minimo',
    },
    multiplier: 0.93,
  },
  {
    value: '3.0',
    labelKey: 'standardIsolation',
    label: {
      en: 'Standard Insulation',
      de: 'Standard-Isolation',
      fr: 'Isolation standard',
      it: 'Isolamento standard',
    },
    multiplier: 0.97,
  },
  {
    value: '4.0',
    labelKey: 'huelleIsoliert',
    label: {
      en: 'Envelope Insulated',
      de: 'Hülle isoliert',
      fr: 'Enveloppe isolée',
      it: 'Involucro isolato',
    },
    multiplier: 1.0,
  },
  {
    value: '5.0',
    labelKey: 'minergieStandard',
    label: {
      en: 'Minergie',
      de: 'Minergie-Standard',
      fr: 'Minergie',
      it: 'Minergie',
    },
    multiplier: 1.05,
  },
  {
    value: '6.0',
    labelKey: 'minergieP',
    label: {
      en: 'Minergie-P',
      de: 'Minergie-P',
      fr: 'Minergie-P',
      it: 'Minergie-P',
    },
    multiplier: 1.1,
  },
  {
    value: '7.0',
    labelKey: 'passivhaus',
    label: {
      en: 'Passive House',
      de: 'Passivhaus',
      fr: 'Maison passive',
      it: 'Casa passiva',
    },
    multiplier: 1.2,
  },
]

/**
 * Lookup maps for quick multiplier access by standard level
 */
export const STANDARD_MULTIPLIERS: Record<string, number> = Object.fromEntries(
  STANDARD_OPTIONS.map((o) => [o.value, o.multiplier])
)

export const ENERGY_MULTIPLIERS: Record<string, number> = Object.fromEntries(
  ENERGY_STANDARD_OPTIONS.map((o) => [o.value, o.multiplier])
)

/**
 * Get the multiplier for a given standard level
 */
export const getStandardMultiplier = (standard: StandardLevel): number =>
  STANDARD_MULTIPLIERS[standard] ?? 1.0

/**
 * Get the multiplier for a given energy standard level
 */
export const getEnergyMultiplier = (
  energyStandard: EnergyStandardLevel
): number => ENERGY_MULTIPLIERS[energyStandard] ?? 1.0
